import type { Session, SupabaseClient } from '@supabase/supabase-js';

const ACCESS_TOKEN_REFRESH_SKEW_MS = 120_000;
const AUTH_RETRY_HEADER = 'x-vuelto-auth-retry';

let supabaseRef: SupabaseClient | null = null;
let refreshInFlight: Promise<Session | null> | null = null;

// Callbacks registrados desde afuera para evitar import circular.
type LogoutFn = (reason: string) => Promise<void> | void;
type SetSessionFn = (session: Session, user: Session['user']) => void;
let onSessionInvalidLogout: LogoutFn | null = null;
let onSessionRefreshed: SetSessionFn | null = null;

/** Registro del cliente creado en supabaseClient (evita import circular). */
export function bindSupabaseClient(client: SupabaseClient): void {
  supabaseRef = client;
}

/** Registra callbacks para evitar imports dinámicos de auth. */
export function bindAuthCallbacks(opts: {
  onSessionInvalidLogout: LogoutFn;
  onSessionRefreshed: SetSessionFn;
}): void {
  onSessionInvalidLogout = opts.onSessionInvalidLogout;
  onSessionRefreshed = opts.onSessionRefreshed;
}

function getSupabase(): SupabaseClient {
  if (!supabaseRef) {
    throw new Error('[Supabase] Cliente no inicializado');
  }
  return supabaseRef;
}

export function isSupabaseJwtExpiredMessage(message: string | undefined): boolean {
  if (!message) return false;
  const m = message.toLowerCase();
  return m.includes('jwt expired') || m.includes('invalid jwt') || m.includes('pgrst303');
}

export function isSupabaseJwtExpiredError(error: { message?: string; code?: string } | null | undefined): boolean {
  if (!error) return false;
  if (error.code === 'PGRST303') return true;
  return isSupabaseJwtExpiredMessage(error.message);
}

function isRefreshTokenInvalidMessage(message: string | undefined): boolean {
  if (!message) return false;
  const m = message.toLowerCase();
  return m.includes('refresh token') || m.includes('refresh_token') || m.includes('session not found');
}

/** Sincroniza Zustand tras renovar (el cliente Supabase ya tiene la sesión nueva). */
export async function refreshSupabaseSessionAndSyncStore(): Promise<Session | null> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const { data, error } = await getSupabase().auth.refreshSession();
    if (error) {
      if (isRefreshTokenInvalidMessage(error.message)) {
        console.warn('[Supabase] refreshSession: sesión inválida, requiere login');
        if (onSessionInvalidLogout) void onSessionInvalidLogout('session_invalid');
      } else {
        console.warn('[Supabase] refreshSession:', error.message);
      }
      return null;
    }
    const session = data.session ?? null;
    if (session && onSessionRefreshed) {
      onSessionRefreshed(session, session.user);
    }
    return session;
  })().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

/**
 * Access token válido para REST/RPC/Edge. Renueva si vence pronto o ya venció.
 */
export async function ensureFreshAccessToken(): Promise<string | null> {
  const {
    data: { session },
  } = await getSupabase().auth.getSession();
  if (!session?.access_token) return null;

  const expiresAtMs = (session.expires_at ?? 0) * 1000;
  const expiredOrSoon = expiresAtMs <= Date.now() + ACCESS_TOKEN_REFRESH_SKEW_MS;

  if (!expiredOrSoon) {
    return session.access_token;
  }

  const refreshed = await refreshSupabaseSessionAndSyncStore();
  return refreshed?.access_token ?? null;
}

/**
 * Fetch global del cliente Supabase: un reintento tras renovar JWT si PostgREST devuelve 401 por token vencido.
 */
export async function supabaseFetchWithAuthRetry(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const headers = new Headers(init?.headers);
  if (headers.get(AUTH_RETRY_HEADER) === '1') {
    return fetch(input, init);
  }

  const response = await fetch(input, init);
  if (response.status !== 401) return response;

  const url =
    typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;

  if (!url.includes('/rest/v1/') && !url.includes('/functions/v1/')) {
    return response;
  }

  let bodyText = '';
  try {
    bodyText = await response.clone().text();
  } catch {
    return response;
  }

  if (!isSupabaseJwtExpiredMessage(bodyText) && !bodyText.includes('PGRST303')) {
    return response;
  }

  const session = await refreshSupabaseSessionAndSyncStore();
  if (!session?.access_token) return response;

  const retryHeaders = new Headers(init?.headers);
  retryHeaders.set(AUTH_RETRY_HEADER, '1');
  return fetch(input, { ...init, headers: retryHeaders });
}
